import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProductDetail, getShipping } from '../api/aliApi';
import { ClipboardIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';

const countryMap = {
  CN: 'China',
  US: 'United States',
  NG: 'Nigeria',
};

const formatPrice = (price) => {
  if (!price) return 'N/A';
  const numeric = parseFloat(price);
  return numeric.toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });
};

const AliProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      const data = await getProductDetail(productId);
      const productData = data?.full?.aliexpress_ds_product_get_response?.result;
      setProduct(productData);

      const sku = productData?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.sku_id;
      if (sku) {
        const ship = await getShipping(productId, sku, 'NG');
        const options = ship?.aliexpress_ds_freight_query_response?.result?.delivery_options?.delivery_option_d_t_o || [];
        setShippingOptions(options);
      }

      setLoading(false);
    };
    load();
  }, [productId]);

  useEffect(() => {
    if (!product) return;
    const imgs = document.querySelectorAll('.description-content img');
    imgs.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '10px auto';
    });
  }, [product]);

  const handleCopyURL = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Product URL copied!');
  };

  const handleWhatsApp = () => {
    const productUrl = window.location.href;
    const deliveryOptionsText = shippingOptions
      .map((option) => `• ${option.company}: ${option.shipping_fee_format}`)
      .join('\n');

    const message = `
Hello! I'm interested in this product:

🛒 *${title}*
💵 *Price:* ₦${formattedPrice} ${currencycode}
🚚 *Delivery Options:*
${deliveryOptionsText}

🔗 *Product Link:* ${productUrl}
    `.trim();

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = '2348024980215';
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  if (loading || !product) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
      </div>
    );
  }

  const images = product?.ae_multimedia_info_dto?.image_urls?.split(';').filter(Boolean) || [];
  const detailHTML = product?.ae_item_base_info_dto?.detail || '';
  const price = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.offer_sale_price || 'N/A';
  const currencycode = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.currency_code;
  const skuavailable = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.sku_available_stock;
  const title = product?.ae_item_base_info_dto?.subject || 'Untitled Product';
  const store = product?.ae_store_info?.store_name || 'Unknown Store';
  const formattedPrice = formatPrice(price);
  const productImage = images[0] || 'https://placehold.co/600x600';
  const productDescription =
    product?.ae_item_base_info_dto?.subject ||
    detailHTML.replace(/<[^>]+>/g, '').slice(0, 160);

  return (
    <>
      {/* SEO Helmet Meta */}
      <Helmet>
        <title>{`${title} | KambiHome - Powered by AliExpress`}</title>
        <meta name="description" content={productDescription} />
        <meta name="keywords" content={`${title}, AliExpress, Nigeria, KambiHome`} />
        <link rel="canonical" href={window.location.href} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={productDescription} />
        <meta property="og:image" content={productImage} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="product" />
      </Helmet>

      <Header />
      <div className="max-w-7xl mt-16 mx-auto px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/2 p-4 relative">
              <img
                src="https://ae01.alicdn.com/kf/Sa0202ec8a96a4085962acfc27e9ffd04F/1080x1080.jpg"
                alt="AliExpress Logo"
                className="absolute top-3 left-3 w-16 rounded-full border-2 border-white shadow-md"
              />
              <div className="rounded-lg overflow-hidden mb-3">
                <img
                  src={images[carouselIndex]}
                  alt={`Main ${carouselIndex}`}
                  className="w-full h-80 object-contain bg-gray-50"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCarouselIndex(idx)}
                    className={`w-8 h-8 text-sm rounded-full border ${
                      idx === carouselIndex ? 'bg-green-600 text-white' : 'bg-white text-gray-700'
                    } hover:bg-green-500 hover:text-white`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>

            <div className="w-full md:w-1/2 p-6">
              <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>
              <p className="text-green-600 text-xl mt-2 font-bold">
                {formattedPrice}{' '}
                <span className="text-sm bg-gray-200 text-gray-800 px-2 py-1 rounded">{currencycode}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Store: <strong>{store}</strong>
              </p>
              <p className="text-xs mt-1 bg-black text-white inline-block px-2 py-1 rounded">
                SKU Stock: {skuavailable}
              </p>

              {shippingOptions.length > 0 && (
                <div className="mt-6 bg-gray-50 p-4 border rounded-md">
                  <h4 className="text-md font-semibold mb-3">Shipping Options to Nigeria</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {shippingOptions.map((option, idx) => (
                      <div key={idx} className="border p-3 rounded bg-white shadow">
                        <p><strong>Carrier:</strong> {option.company}</p>
                        <p><strong>Fee:</strong> {option.shipping_fee_format}</p>
                        <p><strong>ETA:</strong> {option.delivery_date_desc}</p>
                        <p><strong>Delivery Range:</strong> {option.min_delivery_days}–{option.max_delivery_days} days</p>
                        <p><strong>From:</strong> {countryMap[option.ship_from_country] || option.ship_from_country}</p>
                        {option.free_shipping_threshold && (
                          <p><strong>Free Shipping Over:</strong> {option.free_shipping_threshold}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-6 flex-wrap">
                <button
                  onClick={handleWhatsApp}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                >
                  <ShoppingCartIcon className="w-5 h-5" />
                  Buy on WhatsApp
                </button>
                <button
                  onClick={handleCopyURL}
                  className="flex items-center gap-2 border border-gray-700 text-gray-800 px-4 py-2 rounded hover:bg-gray-100"
                >
                  <ClipboardIcon className="w-5 h-5" />
                  Copy URL
                </button>
              </div>
            </div>
          </div>
        </div>

        {detailHTML && (
          <div className="mt-8 bg-white p-6 rounded shadow">
            <h3 className="text-lg font-semibold mb-4">Product Description</h3>
            <div
              className="description-content prose max-w-full overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: detailHTML }}
            />
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default AliProductDetail;
